import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import {
  Trophy, TrendingUp, Target, Award, Flame, Zap, ArrowLeft, CheckCircle2, AlertTriangle,
} from "lucide-react";
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell,
} from "recharts";

const DISMISSAL_COLORS = ["#FF3B30", "#007AFF", "#FF9500", "#34C759", "#AF52DE", "#FFCC00"];
const CATEGORY_ACCENTS = {
  Batting: "#FF3B30", Bowling: "#007AFF", Fielding: "#34C759", Fitness: "#F59E0B",
};

export default function PublicProfile() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function load() {
      try {
        const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/share/${token}`);
        setData(res.data);
      } catch (e) {
        setError(e.response?.data?.detail || "Share link not found");
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [token]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] text-white flex items-center justify-center">
        <div className="text-sm font-mono uppercase tracking-widest text-[#A3A3A3]">Loading player profile...</div>
      </div>
    );
  }
  if (error) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] text-white flex flex-col items-center justify-center gap-4 p-6">
        <AlertTriangle className="w-12 h-12 text-[#FF3B30]" />
        <h1 className="text-2xl font-black uppercase">Link Unavailable</h1>
        <p className="text-sm text-[#A3A3A3]">{error}</p>
        <Link to="/" className="text-[#FF3B30] font-bold text-sm hover:underline">← Back to CricketPulse</Link>
      </div>
    );
  }

  const { player, analytics, ai_report } = data;
  const s = analytics.summary;
  const trends = analytics.trends || [];
  const dismissals = (analytics.dismissals || []).map((d, i) => ({
    ...d, color: DISMISSAL_COLORS[i % DISMISSAL_COLORS.length],
  }));

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      <header className="sticky top-0 z-40 bg-[#141414]/90 backdrop-blur-xl border-b border-[#262626] px-6 py-4 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3">
          <div className="w-10 h-10 rounded bg-[#FF3B30] flex items-center justify-center text-white font-black text-xl">CP</div>
          <div>
            <h1 className="text-xl font-black uppercase tracking-wider">CRICKETPULSE</h1>
            <p className="text-[10px] text-[#A3A3A3] uppercase tracking-widest">Public Player Profile</p>
          </div>
        </Link>
        <Link to="/register" data-testid="public-cta-signup" className="bg-[#FF3B30] hover:bg-[#E03229] text-white px-4 py-2 rounded text-xs font-bold shadow-lg">
          Track Your Own Stats
        </Link>
      </header>

      <main className="max-w-6xl mx-auto p-6 md:p-8 space-y-8">
        {/* Player Header */}
        <div className="bg-gradient-to-r from-[#141414] to-[#1A1A1A] border border-[#262626] rounded-2xl p-8 flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-5">
            <div className="w-20 h-20 rounded-full bg-[#FF3B30] flex items-center justify-center text-white font-black text-2xl uppercase shadow-[0_0_20px_rgba(255,59,48,0.4)]">
              {(player.name || "P").slice(0, 2)}
            </div>
            <div>
              <div className="text-[10px] font-mono uppercase text-[#FF3B30] tracking-widest mb-1">Verified Player Profile</div>
              <h2 data-testid="public-player-name" className="text-3xl md:text-4xl font-black uppercase text-white">{player.name}</h2>
              <p className="text-sm text-[#A3A3A3] mt-1">
                {player.profile?.playing_role} • {player.profile?.batting_style}
                {player.profile?.bowling_style && player.profile.bowling_style !== "None"
                  ? ` • ${player.profile.bowling_style}` : ""}
                {player.profile?.team ? ` • ${player.profile.team}` : ""}
              </p>
            </div>
          </div>
          <div className="bg-[#0A0A0A] border border-[#262626] rounded-xl p-5 w-full md:w-72 space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-[#A3A3A3]">Batting Average</span><span className="font-bold text-white">{s.batting_average}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-[#A3A3A3]">Strike Rate</span><span className="font-bold text-emerald-400">{s.strike_rate}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-[#A3A3A3]">Bowling Economy</span><span className="font-bold text-white">{s.bowling_economy}</span>
            </div>
            <div className="flex justify-between text-sm border-t border-[#262626] pt-2 mt-2">
              <span className="text-[#A3A3A3]">Form Rating</span>
              <span className="text-emerald-400 font-bold flex items-center gap-1"><Flame className="w-3 h-3" /> {s.consistency_score}</span>
            </div>
          </div>
        </div>

        {/* Stat Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <PublicStat label="Matches" value={s.total_matches} icon={Trophy} color="#FF3B30" />
          <PublicStat label="Total Runs" value={s.total_runs} icon={TrendingUp} color="#34C759" />
          <PublicStat label="Wickets" value={s.total_wickets} icon={Target} color="#007AFF" />
          <PublicStat label="Best Score" value={s.best_score} icon={Award} color="#FFCC00" />
        </div>

        {/* Charts */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 bg-[#141414] border border-[#262626] rounded-xl p-6">
            <h3 className="text-lg font-bold uppercase text-white mb-4">Runs Trend</h3>
            <div className="h-64">
              {trends.length === 0 ? <Empty /> : (
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={trends}>
                    <XAxis dataKey="match" stroke="#737373" fontSize={11} tickLine={false} />
                    <YAxis stroke="#737373" fontSize={11} tickLine={false} />
                    <Tooltip contentStyle={{ backgroundColor: "#141414", borderColor: "#262626", borderRadius: 8 }} />
                    <Line type="monotone" dataKey="runs" stroke="#FF3B30" strokeWidth={3} dot={{ fill: "#FF3B30", r: 4 }} />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>
          <div className="bg-[#141414] border border-[#262626] rounded-xl p-6">
            <h3 className="text-lg font-bold uppercase text-white mb-4">Dismissals</h3>
            <div className="h-48">
              {dismissals.length === 0 ? <Empty /> : (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={dismissals} innerRadius={40} outerRadius={70} paddingAngle={4} dataKey="value">
                      {dismissals.map((d, i) => <Cell key={i} fill={d.color} />)}
                    </Pie>
                    <Tooltip contentStyle={{ backgroundColor: "#141414", borderColor: "#262626", borderRadius: 8 }} />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </div>
            <div className="flex flex-wrap gap-2 justify-center pt-2">
              {dismissals.map((d) => (
                <span key={d.name} className="text-[11px] flex items-center gap-1.5 text-[#A3A3A3]">
                  <span className="w-2 h-2 rounded-full" style={{ backgroundColor: d.color }} />
                  {d.name} ({d.value})
                </span>
              ))}
            </div>
          </div>
        </div>

        {/* AI Coach Report */}
        {ai_report ? (
          <div className="space-y-6" data-testid="public-ai-report">
            <div className="bg-gradient-to-r from-[#141414] via-[#1A1A1A] to-[#141414] border border-[#FF3B30]/30 rounded-2xl p-6">
              <div className="flex items-center gap-2 mb-2">
                <span className="bg-yellow-400/20 text-yellow-400 text-[10px] font-mono font-bold px-2.5 py-1 rounded border border-yellow-400/30 uppercase flex items-center gap-1">
                  <Zap className="w-3 h-3" /> AI Coach Dossier
                </span>
                <span className="text-[10px] text-[#A3A3A3] font-mono">Form Rating: {ai_report.form_rating}/100</span>
              </div>
              <p className="text-base text-white leading-relaxed">{ai_report.summary}</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <ReportBlock icon={<CheckCircle2 className="w-5 h-5" />} title="Strengths" items={ai_report.strengths || []} accent="emerald" />
              <ReportBlock icon={<AlertTriangle className="w-5 h-5" />} title="Areas To Improve" items={ai_report.weaknesses || []} accent="red" />
            </div>
            {(ai_report.training_recommendations || []).length > 0 && (
              <div className="bg-[#141414] border border-[#262626] rounded-xl p-6">
                <h4 className="text-lg font-bold uppercase text-white mb-4">Training Prescriptions</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {ai_report.training_recommendations.map((r, i) => (
                    <div key={i} className="bg-[#1A1A1A] border border-[#262626] rounded-lg p-4 space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-[10px] font-mono uppercase font-bold" style={{ color: CATEGORY_ACCENTS[r.category] || "#fff" }}>
                          {r.category}
                        </span>
                        <span className="text-[10px] bg-[#262626] px-2 py-0.5 rounded">{r.priority}</span>
                      </div>
                      <h5 className="text-sm font-bold text-white">{r.title}</h5>
                      <p className="text-[11px] text-[#A3A3A3] leading-relaxed">{r.detail}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="text-center text-sm text-[#A3A3A3] bg-[#141414] border border-dashed border-[#262626] rounded-xl p-8">
            This player hasn't generated an AI Coach report yet.
          </div>
        )}

        <div className="text-center py-8 border-t border-[#262626]">
          <p className="text-xs text-[#A3A3A3] mb-2">Impressed? Track your own cricket performance.</p>
          <Link to="/register" className="inline-flex items-center gap-2 bg-[#FF3B30] hover:bg-[#E03229] text-white px-6 py-2.5 rounded text-sm font-bold shadow-lg">
            Sign Up Free →
          </Link>
        </div>
      </main>
    </div>
  );
}

function PublicStat({ label, value, icon: Icon, color }) {
  return (
    <div className="bg-[#141414] border border-[#262626] rounded-xl p-4 space-y-1">
      <div className="flex items-center justify-between text-[#A3A3A3]">
        <span className="text-[10px] uppercase font-mono tracking-wider">{label}</span>
        <Icon className="w-4 h-4" style={{ color }} />
      </div>
      <div className="text-2xl font-black text-white">{value ?? 0}</div>
    </div>
  );
}

function ReportBlock({ icon, title, items, accent }) {
  const border = accent === "emerald" ? "border-emerald-500/30" : "border-[#FF3B30]/30";
  const bg = accent === "emerald" ? "bg-emerald-500/20 text-emerald-400" : "bg-[#FF3B30]/20 text-[#FF3B30]";
  return (
    <div className={`bg-[#141414] border ${border} rounded-xl p-5 space-y-3`}>
      <div className="flex items-center gap-2 border-b border-[#262626] pb-3">
        <div className={`w-8 h-8 rounded flex items-center justify-center ${bg}`}>{icon}</div>
        <h4 className="text-base font-bold text-white uppercase">{title}</h4>
      </div>
      <ul className="space-y-2 text-xs text-[#A3A3A3]">
        {items.length === 0 && <li className="italic">No items.</li>}
        {items.map((it, i) => (
          <li key={i}>
            <strong className="text-white block">{it.title}</strong>
            {it.detail}
          </li>
        ))}
      </ul>
    </div>
  );
}

function Empty() {
  return <div className="w-full h-full flex items-center justify-center text-xs text-[#737373] font-mono uppercase">No data yet</div>;
}
